import React from 'react';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { tenantProfileLabelMap } from '@types';
import type { TenantProfileLabelKey, ToneType } from '@types';
import { formatNumber } from '@utils/parsing';

interface DataRowProps {
    field: TenantProfileLabelKey;
    label?: string;
    value: unknown;
    tone?: ToneType;
    display?: 'flex' | 'none';
    href?: string;
}

const RESULT_FIELDS: TenantProfileLabelKey[] = ['profitAfterFinancialItems', 'netResult'];

const BOOLEAN_FIELDS: TenantProfileLabelKey[] = [
    'vat',
    'fTax',
    'employerContributions'
];

const POSITIVE_NEGATIVE_BOOLEAN_FIELDS: TenantProfileLabelKey[] = [
    'vat',
    'fTax',
    'employerContributions'
];

const LONG_STRING_FIELDS: TenantProfileLabelKey[] = [
    'companyStatus'
];

const formatCompanyStatus = (status: string): string => {
    return status.replace(/(\d{4}-\d{2}-\d{2})(?=[A-Za-z\u00C0-\u017F])/g, '$1\n');
};

const formatBoolean = (value: boolean | null | undefined) => {
    if (value === true) return 'Ja';
    if (value === false) return 'Nej';
    return '-';
};

const formatGenericValue = (value: unknown): string | number => {
    if (value == null || value === '') return '-';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
    if (typeof value === 'boolean') return formatBoolean(value);
    if (typeof value === 'number') return formatNumber(value);
    if (typeof value === 'string') return value;
    return String(value);
};

const formatValue = (field: TenantProfileLabelKey, value: unknown): string | number => {
    if (value == null || value === '') return '-';

    if (BOOLEAN_FIELDS.includes(field)) {
        return formatBoolean(value as boolean | null | undefined);
    }

    if (RESULT_FIELDS.includes(field) && typeof value === 'number') {
        return formatNumber(value, 'SEK');
    }

    if (LONG_STRING_FIELDS.includes(field) && typeof value === 'string') {
        return formatCompanyStatus(value);
    }

    return formatGenericValue(value);
};

const resolveTone = ({
    field,
    value
}: {
    field: TenantProfileLabelKey;
    value: unknown;
}): ToneType => {
    if (typeof value === 'boolean') {
        if (POSITIVE_NEGATIVE_BOOLEAN_FIELDS.includes(field)) {
            return value === true ? 'good' : 'bad';
        }
    }

    if (typeof value === 'number') {
        if (RESULT_FIELDS.includes(field) && typeof value === 'number') {
            if (value < 0) return 'bad';
            return 'neutral';
        }
    }

    if (field === 'companyStatus' && typeof value === 'string') {
        const status = value.trim().toLowerCase();
        if (['aktiv', 'verksam'].some((s) => status.includes(s))) {
            return 'good';
        }
        if (['konkurs', 'likvidation'].some((s) => status.includes(s))) {
            return 'bad';
        }
        return 'neutral';
    }

    return 'neutral';
};

const toneColor = (tone?: ToneType, href?: string) => {
    if (href) return 'primary.main';
    switch (tone) {
        case 'good': return 'success.main';
        case 'bad': return 'error.main';
        default: return 'text.primary';
    }
};

const DataRow: React.FC<DataRowProps> = (props) => {
    const {
        field,
        label,
        value,
        tone,
        display = 'flex',
        href
    } = props;
    const resolvedLabel = label ?? (field ? tenantProfileLabelMap[field] : '-');
    const resolvedValue = field ? formatValue(field, value) : formatGenericValue(value);
    const resolvedTone = tone ?? resolveTone({ field, value });
    return (
        <Box
            sx={{
                display,
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 }
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {resolvedLabel}
            </Typography>
            <Box
                component="span"
                sx={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 500,
                    fontSize: 14,
                    color: toneColor(resolvedTone, href),
                    whiteSpace: 'pre-line',
                    textAlign: 'right'
                }}
            >
                {href ? (
                    <a href={href} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {resolvedValue}
                    </a>
                ) : (
                    resolvedValue
                )}
            </Box>
        </Box>
    );
};

export default DataRow;
